import React, { useState, useEffect } from 'react';
import { academiasAPI, professoresAPI, cepAPI } from '../services/api';
import Modal from '../components/Modal';

function Academias() {
  const [academias, setAcademias] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({});
  const [formData, setFormData] = useState({
    nome: '',
    cep: '',
    endereco: '',
    bairro: '',
    numero: '',
    complemento: '',
    cidade: '',
    estado: ''
  });

  // Gerenciamento de Professores/Filiais
  const [showProfessorModal, setShowProfessorModal] = useState(false);
  const [selectedAcademiaForProfessores, setSelectedAcademiaForProfessores] = useState(null);
  const [showProfessorForm, setShowProfessorForm] = useState(false);
  const [editingProfessorId, setEditingProfessorId] = useState(null);
  const [buscandoCepProfessor, setBuscandoCepProfessor] = useState(false);
  const [professorData, setProfessorData] = useState({
    nome: '',
    telefone: '',
    documento: '',
    is_filial: false,
    nome_filial: '',
    cep: '',
    endereco: '',
    bairro: '',
    numero: '',
    complemento: '',
    cidade: '',
    estado: ''
  });

  useEffect(() => {
    loadAcademias();
  }, []);

  const loadAcademias = async () => {
    try {
      const [academiasRes, professoresRes] = await Promise.all([
        academiasAPI.getAll(),
        professoresAPI.getAll()
      ]);
      setAcademias(academiasRes.data);
      setProfessores(professoresRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar academias:', error);
      setLoading(false);
    }
  };

  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      setBuscandoCep(true);
      try {
        const response = await cepAPI.buscar(cepLimpo);
        setFormData(prev => ({
          ...prev,
          endereco: response.data.endereco || '',
          bairro: response.data.bairro || '',
          cidade: response.data.cidade || '',
          estado: response.data.estado || ''
        }));
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        setModalConfig({
          type: 'error',
          title: 'CEP não encontrado',
          message: 'Não foi possível encontrar o endereço para este CEP. Verifique e tente novamente.',
          onConfirm: () => setModalOpen(false)
        });
        setModalOpen(true);
      } finally {
        setBuscandoCep(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await academiasAPI.update(editingId, formData);
        setModalConfig({
          type: 'success',
          title: 'Academia Atualizada',
          message: 'As informações da academia foram atualizadas com sucesso.',
          onConfirm: () => {
            setModalOpen(false);
            loadAcademias();
            resetForm();
          }
        });
        setModalOpen(true);
      } else {
        await academiasAPI.create(formData);
        setModalConfig({
          type: 'success',
          title: 'Academia Cadastrada',
          message: 'Nova academia adicionada com sucesso ao sistema.',
          onConfirm: () => {
            setModalOpen(false);
            loadAcademias();
            resetForm();
          }
        });
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Erro ao salvar academia:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro ao Salvar',
        message: 'Não foi possível salvar as informações da academia.\nVerifique os dados e tente novamente.',
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  const handleEdit = (academia) => {
    setFormData({
      nome: academia.nome,
      cep: academia.cep || '',
      endereco: academia.endereco || '',
      bairro: academia.bairro || '',
      numero: academia.numero || '',
      complemento: academia.complemento || '',
      cidade: academia.cidade,
      estado: academia.estado
    });
    setEditingId(academia.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const academia = academias.find(a => a.id === id);
    setModalConfig({
      type: 'confirm',
      title: 'Confirmar Exclusão',
      message: `Deseja realmente excluir a academia "${academia?.nome}"?\n\nAtenção: Esta ação pode afetar alunos vinculados a esta academia.\n\nEsta ação não poderá ser desfeita.`,
      showCancel: true,
      onConfirm: async () => {
        try {
          await academiasAPI.delete(id);
          loadAcademias();
          setModalConfig({
            type: 'success',
            title: 'Sucesso',
            message: 'Academia excluída com sucesso!',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        } catch (error) {
          console.error('Erro ao excluir academia:', error);
          setModalConfig({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao excluir academia. Verifique se não há alunos vinculados.',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        }
      }
    });
    setModalOpen(true);
  };

  // Funções de Professores/Filiais
  const buscarCepProfessor = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      setBuscandoCepProfessor(true);
      try {
        const response = await cepAPI.buscar(cepLimpo);
        setProfessorData(prev => ({
          ...prev,
          endereco: response.data.endereco || '',
          bairro: response.data.bairro || '',
          cidade: response.data.cidade || '',
          estado: response.data.estado || ''
        }));
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      } finally {
        setBuscandoCepProfessor(false);
      }
    }
  };

  const handleProfessorSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...professorData, academia_id: selectedAcademiaForProfessores };
      if (editingProfessorId) {
        await professoresAPI.update(editingProfessorId, dataToSend);
        setModalConfig({
          type: 'success',
          title: 'Professor/Filial Atualizado',
          message: 'As informações foram atualizadas com sucesso!',
          onConfirm: () => {
            setModalOpen(false);
            loadAcademias();
            resetProfessorForm();
          }
        });
        setModalOpen(true);
      } else {
        await professoresAPI.create(dataToSend);
        setModalConfig({
          type: 'success',
          title: 'Professor/Filial Cadastrado',
          message: 'Professor/Filial cadastrado com sucesso!',
          onConfirm: () => {
            setModalOpen(false);
            loadAcademias();
            resetProfessorForm();
          }
        });
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Erro ao salvar professor:', error);
      setModalConfig({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao salvar professor/filial. Tente novamente.',
        onConfirm: () => setModalOpen(false)
      });
      setModalOpen(true);
    }
  };

  const handleEditProfessor = (professor) => {
    setProfessorData({
      nome: professor.nome,
      telefone: professor.telefone,
      documento: professor.documento,
      is_filial: professor.is_filial,
      nome_filial: professor.nome_filial || '',
      cep: professor.cep || '',
      endereco: professor.endereco || '',
      bairro: professor.bairro || '',
      numero: professor.numero || '',
      complemento: professor.complemento || '',
      cidade: professor.cidade || '',
      estado: professor.estado || ''
    });
    setEditingProfessorId(professor.id);
    setShowProfessorForm(true);
  };

  const handleDeleteProfessor = async (id, nome) => {
    setModalConfig({
      type: 'confirm',
      title: 'Confirmar Exclusão',
      message: `Deseja realmente excluir ${nome}?\n\nEsta ação não poderá ser desfeita.`,
      showCancel: true,
      onConfirm: async () => {
        try {
          await professoresAPI.delete(id);
          loadAcademias();
          setModalConfig({
            type: 'success',
            title: 'Sucesso',
            message: 'Professor/Filial excluído com sucesso!',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        } catch (error) {
          console.error('Erro ao excluir professor:', error);
          setModalConfig({
            type: 'error',
            title: 'Erro',
            message: 'Erro ao excluir. Verifique se não há alunos vinculados.',
            onConfirm: () => setModalOpen(false)
          });
          setModalOpen(true);
        }
      }
    });
    setModalOpen(true);
  };

  const resetProfessorForm = () => {
    setProfessorData({
      nome: '',
      telefone: '',
      documento: '',
      is_filial: false,
      nome_filial: '',
      cep: '',
      endereco: '',
      bairro: '',
      numero: '',
      complemento: '',
      cidade: '',
      estado: ''
    });
    setEditingProfessorId(null);
    setShowProfessorForm(false);
  };

  const getProfessoresDaAcademia = (academiaId) => {
    return professores.filter(p => p.academia_id === academiaId);
  };

  const resetForm = () => {
    setFormData({ 
      nome: '', 
      cep: '',
      endereco: '',
      bairro: '',
      numero: '',
      complemento: '',
      cidade: '', 
      estado: '' 
    });
    setEditingId(null);
    setShowForm(false);
  };


  const academiasFiltradas = academias.filter(academia => {
    return !searchTerm || 
      academia.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      academia.cidade.toLowerCase().includes(searchTerm.toLowerCase()) ||
      academia.estado.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Gerenciamento de Academias</h1>
        <p>Cadastre e gerencie as academias participantes</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nova Academia'}
        </button>
      </div>

      {!showForm && (
        <div className="search-filters-container" style={{ marginBottom: '20px' }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Buscar por nome, cidade ou estado..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Limpar busca"
              >
                ✕
              </button>
            )}
          </div>
          <div className="pagination">
            <span>Mostrando <strong>{academiasFiltradas.length}</strong> de <strong>{academias.length}</strong> academias</span>
          </div>
        </div>
      )}

      {showForm && (
        <div className="form-container" style={{ marginBottom: '30px' }}>
          <h2>{editingId ? 'Editar Academia' : 'Nova Academia'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nome *</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
              />
            </div>

            <h3 style={{ marginTop: '20px', marginBottom: '15px', color: '#333' }}>📍 Endereço</h3>

            <div className="form-group">
              <label>CEP</label>
              <input
                type="text"
                value={formData.cep}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({...formData, cep: value});
                  if (value.replace(/\D/g, '').length === 8) {
                    buscarCep(value);
                  }
                }}
                placeholder="00000-000"
                maxLength="9"
              />
              {buscandoCep && <small>Buscando CEP...</small>}
            </div>

            <div className="form-group">
              <label>Endereço</label>
              <input
                type="text"
                value={formData.endereco}
                onChange={(e) => setFormData({...formData, endereco: e.target.value})}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Bairro</label>
                <input
                  type="text"
                  value={formData.bairro}
                  onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Número</label>
                <input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => setFormData({...formData, numero: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Complemento</label>
              <input
                type="text"
                value={formData.complemento}
                onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                placeholder="Apto, Sala, etc"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Cidade *</label>
                <input
                  type="text"
                  value={formData.cidade}
                  onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Estado *</label>
                <input
                  type="text"
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                  maxLength="2"
                  required
                />
              </div>
            </div>

            <div className="actions">
              <button type="submit" className="btn btn-success">
                {editingId ? 'Atualizar' : 'Cadastrar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Endereço</th>
                <th>Cidade</th>
                <th>Estado</th>
                <th>Professores/Filiais</th>
                <th>Ações</th>
              </tr>
            </thead>
          <tbody>
            {academiasFiltradas.map(academia => (
              <React.Fragment key={academia.id}>
                <tr>
                  <td>{academia.nome}</td>
                  <td>
                    {academia.endereco && (
                      <div style={{ fontSize: '0.9em' }}>
                        <div>{academia.endereco}, {academia.numero}</div>
                        {academia.bairro && <div>{academia.bairro}</div>}
                        {academia.cep && <div>CEP: {academia.cep}</div>}
                      </div>
                    )}
                    {!academia.endereco && <span style={{ color: '#999' }}>-</span>}
                  </td>
                  <td>{academia.cidade}</td>
                  <td>{academia.estado}</td>
                  <td>
                    <button
                      onClick={() => {
                        setSelectedAcademiaForProfessores(academia.id);
                        setShowProfessorModal(true);
                        setShowProfessorForm(false);
                      }}
                      style={{ 
                        padding: '5px 12px', 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: '12px',
                        fontSize: '0.9em',
                        color: '#1976d2',
                        fontWeight: 'bold',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#bbdefb'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                    >
                      👥 {getProfessoresDaAcademia(academia.id).length} {getProfessoresDaAcademia(academia.id).length === 1 ? 'professor' : 'professores'}
                    </button>
                  </td>
                  <td>
                    <div className="actions">
                      <button 
                        className="btn-primary" 
                        onClick={() => handleEdit(academia)}
                        style={{ marginRight: '8px' }}
                      >
                        Editar
                      </button>
                      <button 
                        className="btn-danger-ghost" 
                        onClick={() => handleDelete(academia.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Modal de Professores */}
      {showProfessorModal && selectedAcademiaForProfessores && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => {
          setShowProfessorModal(false);
          setShowProfessorForm(false);
          resetProfessorForm();
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '1000px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <h2 style={{ margin: 0, color: '#333' }}>
                👨‍🏫 Professores e Filiais - {academias.find(a => a.id === selectedAcademiaForProfessores)?.nome}
              </h2>
              <button
                onClick={() => {
                  setShowProfessorModal(false);
                  setShowProfessorForm(false);
                  resetProfessorForm();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0 10px'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <button 
                className="btn btn-success"
                onClick={() => {
                  resetProfessorForm();
                  setShowProfessorForm(true);
                }}
                style={{ marginBottom: '20px' }}
              >
                + Novo Professor/Filial
              </button>

              {/* Formulário de Professor */}
              {showProfessorForm && (
                <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '8px' }}>
                  <h4 style={{ marginTop: 0 }}>{editingProfessorId ? 'Editar' : 'Novo'} Professor/Filial</h4>
                  <form onSubmit={handleProfessorSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Nome *</label>
                        <input
                          type="text"
                          value={professorData.nome}
                          onChange={(e) => setProfessorData({...professorData, nome: e.target.value})}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Telefone</label>
                        <input
                          type="text"
                          value={professorData.telefone}
                          onChange={(e) => setProfessorData({...professorData, telefone: e.target.value})}
                          placeholder="(00) 00000-0000"
                        />
                      </div>

                      <div className="form-group">
                        <label>Documento (CPF/CNPJ)</label>
                        <input
                          type="text"
                          value={professorData.documento}
                          onChange={(e) => setProfessorData({...professorData, documento: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={professorData.is_filial}
                            onChange={(e) => setProfessorData({...professorData, is_filial: e.target.checked})}
                          />
                          É uma Filial?
                        </label>
                      </div>
                    </div>

                    {professorData.is_filial && (
                      <div className="form-group">
                        <label>Nome da Filial</label>
                        <input
                          type="text"
                          value={professorData.nome_filial}
                          onChange={(e) => setProfessorData({...professorData, nome_filial: e.target.value})}
                          placeholder="Ex: Academia Master - Filial Centro"
                        />
                      </div>
                    )}

                    <h4 style={{ marginTop: '20px', marginBottom: '15px', color: '#333' }}>📍 Endereço</h4>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>CEP</label>
                        <input
                          type="text"
                          value={professorData.cep}
                          onChange={(e) => {
                            const value = e.target.value;
                            setProfessorData({...professorData, cep: value});
                            if (value.replace(/\D/g, '').length === 8) {
                              buscarCepProfessor(value);
                            }
                          }}
                          placeholder="00000-000"
                          maxLength="9"
                        />
                        {buscandoCepProfessor && <small>Buscando CEP...</small>}
                      </div>

                      <div className="form-group">
                        <label>Endereço</label>
                        <input
                          type="text"
                          value={professorData.endereco}
                          onChange={(e) => setProfessorData({...professorData, endereco: e.target.value})}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Bairro</label>
                        <input
                          type="text"
                          value={professorData.bairro}
                          onChange={(e) => setProfessorData({...professorData, bairro: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Número</label>
                        <input
                          type="text"
                          value={professorData.numero}
                          onChange={(e) => setProfessorData({...professorData, numero: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Complemento</label>
                      <input
                        type="text"
                        value={professorData.complemento}
                        onChange={(e) => setProfessorData({...professorData, complemento: e.target.value})}
                        placeholder="Apto, Sala, etc"
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Cidade</label>
                        <input
                          type="text"
                          value={professorData.cidade}
                          onChange={(e) => setProfessorData({...professorData, cidade: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Estado</label>
                        <input
                          type="text"
                          value={professorData.estado}
                          onChange={(e) => setProfessorData({...professorData, estado: e.target.value.toUpperCase()})}
                          maxLength="2"
                        />
                      </div>
                    </div>

                    <div className="actions" style={{ marginTop: '15px' }}>
                      <button type="submit" className="btn btn-success">
                        {editingProfessorId ? 'Atualizar' : 'Cadastrar'}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={resetProfessorForm}>
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Lista de Professores */}
              {getProfessoresDaAcademia(selectedAcademiaForProfessores).length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Telefone</th>
                      <th>Documento</th>
                      <th>Tipo</th>
                      <th>Endereço</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getProfessoresDaAcademia(selectedAcademiaForProfessores).map(prof => (
                      <tr key={prof.id}>
                        <td>{prof.nome}</td>
                        <td>{prof.telefone || '-'}</td>
                        <td>{prof.documento || '-'}</td>
                        <td>
                          {prof.is_filial ? (
                            <span style={{ color: '#2196F3', fontWeight: 'bold' }}>
                              🏢 {prof.nome_filial || 'Filial'}
                            </span>
                          ) : (
                            <span style={{ color: '#4CAF50' }}>👨‍🏫 Professor</span>
                          )}
                        </td>
                        <td>
                          {prof.endereco ? (
                            <div style={{ fontSize: '0.85em' }}>
                              <div>{prof.endereco}, {prof.numero}</div>
                              <div>{prof.bairro} - {prof.cidade}/{prof.estado}</div>
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td>
                          <div className="actions">
                            <button 
                              className="btn-primary" 
                              style={{ fontSize: '0.85em', padding: '5px 10px', marginRight: '8px' }}
                              onClick={() => handleEditProfessor(prof)}
                            >
                              Editar
                            </button>
                            <button 
                              className="btn-danger-ghost" 
                              style={{ fontSize: '0.85em', padding: '5px 10px' }}
                              onClick={() => handleDeleteProfessor(prof.id, prof.nome)}
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ textAlign: 'center', color: '#999', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                  Nenhum professor/filial cadastrado para esta academia.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        {...modalConfig}
      />
    </div>
  );
}

export default Academias;
